import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Volume2, CheckCircle2, ArrowRight } from "lucide-react";
import { speak, speakSequence, cancelSpeech } from "@/lib/speak";

// Reusable phonics engine: explore (tap word + each sound) and blend (choose the word).
export default function PhonicsEngine({ activity, onComplete, autoplay = false }) {
  const cfg = activity.config || {};
  const isBlend = cfg.mode === "blend";
  const [tappedSounds, setTappedSounds] = useState([]);
  const [wordHeard, setWordHeard] = useState(false);
  const [choice, setChoice] = useState(null);
  const advanced = useRef(false);

  const allExplored = wordHeard && tappedSounds.length >= (cfg.sounds?.length || 0);
  const answered = choice !== null;
  const correct = isBlend ? choice === cfg.answer : allExplored;
  const canContinue = isBlend ? (answered && correct) : allExplored;

  useEffect(() => {
    setTappedSounds([]); setWordHeard(false); setChoice(null); advanced.current = false;
    if (autoplay) {
      const items = [{ text: activity.prompt }, { text: cfg.word, before: () => setWordHeard(true) }];
      (cfg.sounds || []).forEach((s, i) => items.push({
        text: s.say,
        before: () => setTappedSounds((prev) => (prev.includes(i) ? prev : [...prev, i])),
      }));
      if (isBlend) {
        items.push({ text: "Which word did the sounds make?" });
        (cfg.options || []).forEach((opt) => items.push({ text: opt }));
      }
      const cancel = speakSequence(items, { speed: 0.8, gap: 400 });
      return () => { cancel(); cancelSpeech(); };
    }
    return () => cancelSpeech();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activity.id, autoplay]);

  useEffect(() => {
    if (autoplay && canContinue && !advanced.current) {
      advanced.current = true;
      speak("Great job!", "nova", { speed: 0.95 });
      const t = setTimeout(() => onComplete(100), 1500);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoplay, canContinue]);

  const tapSound = (i, say) => {
    setTappedSounds((s) => (s.includes(i) ? s : [...s, i]));
    speak(say, "nova", { speed: 0.75 });
  };

  return (
    <div className="text-center" data-testid="phonics-engine">
      <p className="font-body text-base sm:text-lg text-[#6B7280]">{activity.prompt}</p>

      <button
        onClick={() => { setWordHeard(true); speak(cfg.word); }}
        data-testid="phonics-word"
        className="mx-auto mt-6 flex items-center gap-3 rounded-3xl bg-[#0284C7] px-8 py-6 font-heading text-4xl sm:text-5xl font-extrabold text-white shadow-lg transition-transform hover:scale-[1.02] active:scale-95">
        {cfg.word}
        <Volume2 size={30} className="opacity-90" />
      </button>

      <div className="mt-8 flex flex-wrap justify-center gap-3">
        {(cfg.sounds || []).map((s, i) => (
          <motion.button
            key={i}
            whileTap={{ scale: 0.9 }}
            onClick={() => tapSound(i, s.say)}
            data-testid={`phonics-sound-${i}`}
            className={`flex h-20 w-20 flex-col items-center justify-center rounded-2xl border-2 font-heading text-2xl font-extrabold transition-colors ${
              tappedSounds.includes(i)
                ? "border-[#10B981] bg-[#10B981]/10 text-[#047857]"
                : "border-[#E5DECF] bg-white text-[#1F2937] hover:border-[#0284C7]"
            }`}>
            {s.g}
            <Volume2 size={14} className="mt-1 opacity-70" />
          </motion.button>
        ))}
      </div>

      {isBlend && allExplored && (
        <div className="mt-8">
          <p className="font-body text-sm font-semibold uppercase tracking-wide text-[#94A3B8]">Which word did the sounds make?</p>
          <div className="mt-3 flex flex-wrap justify-center gap-3">
            {(cfg.options || []).map((opt, i) => {
              const chosen = choice === i;
              const showState = answered && (i === cfg.answer || chosen);
              const good = i === cfg.answer;
              return (
                <button
                  key={i}
                  disabled={answered}
                  onClick={() => { setChoice(i); speak(opt); }}
                  data-testid={`phonics-option-${i}`}
                  className={`rounded-2xl border-2 px-6 py-3 font-heading text-xl font-bold transition-colors ${
                    showState
                      ? good ? "border-[#10B981] bg-[#10B981]/10 text-[#047857]" : "border-[#E05A47] bg-[#E05A47]/10 text-[#E05A47]"
                      : "border-[#E5DECF] bg-white text-[#1F2937] hover:border-[#0284C7]"
                  }`}>
                  {opt}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-10">
        {(isBlend ? (answered && correct) : allExplored) ? (
          <button
            onClick={() => onComplete(100)}
            data-testid="phonics-continue"
            className="inline-flex items-center gap-2 rounded-full bg-[#10B981] px-8 py-3 font-heading font-bold text-white shadow-md transition-transform hover:scale-105 active:scale-95">
            <CheckCircle2 size={18} /> Great job! Next <ArrowRight size={18} />
          </button>
        ) : isBlend && answered && !correct ? (
          <button onClick={() => setChoice(null)} data-testid="phonics-retry" className="rounded-full bg-[#F59E0B] px-8 py-3 font-heading font-bold text-white">Try again</button>
        ) : (
          <p className="font-body text-sm text-[#94A3B8]">
            {isBlend ? "Tap the word, tap each sound, then pick the word." : "Tap the word and every sound to continue."}
          </p>
        )}
      </div>
    </div>
  );
}
